import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Input, Select, Card, Badge } from "../components/ui";
import { CapGate } from "../components/AutomationCard";
import { Network, Plus, Trash2, RefreshCw, Users, Coins, Send, Scale, History, Play, KeyRound, Copy, Download, FileText, Boxes, ArrowLeftRight, Upload } from "lucide-react";

const APPS = [{ k: "family_tree", l: "Family Tree" }, { k: "holdings", l: "Holdings" }, { k: "currency", l: "Currency" }, { k: "benefits", l: "Benefits" }];
const appL = (k) => APPS.find((a) => a.k === k)?.l || k;
const money = (n) => "$" + Number(n || 0).toLocaleString();

export default function ClusterControl() {
  const [clusters, setClusters] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState("");
  const [newName, setNewName] = useState("");
  const [newPrime, setNewPrime] = useState("");

  const load = useCallback(() => {
    client.get("/clusters").then((r) => setClusters(r.data)).catch((e) => setMsg(apiErr(e)));
    client.get("/licenses").then((r) => setLicenses(r.data.filter((l) => l.assigned_to))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = clusters.find((c) => c.id === sel) || null;
  const licLabel = (l) => l.instance_name || `${l.assigned_to_email} · ${appL(l.app_type)}`;

  const createCluster = async () => {
    if (!newName.trim() || !newPrime) { setMsg("Enter a name and choose a Prime instance."); return; }
    try {
      const { data } = await client.post("/clusters", { name: newName, prime_license_id: newPrime });
      setNewName(""); setNewPrime(""); setSel(data.id); load();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const delCluster = async (id) => {
    if (!window.confirm("Delete this cluster and all its links?")) return;
    try { await client.delete(`/clusters/${id}`); if (sel === id) setSel(null); load(); } catch (e) { setMsg(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="cluster-control">
      <div className="flex items-center gap-3">
        <Network className="text-[var(--primary)]" />
        <div>
          <h1 className="font-head text-2xl">Prime / Cluster Control</h1>
          <p className="text-sm opacity-60">Link up to 12 member instances under one Prime, over a secure challenge-handshake link.</p>
        </div>
      </div>
      {msg && <p className="text-sm text-[var(--primary)]" data-testid="cluster-msg">{msg}</p>}

      <CapGate scope="cluster" title="Cluster operations manage your connected same-app units.">
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        {/* left: clusters + create */}
        <div className="space-y-4">
          <Card className="space-y-3">
            <div className="font-medium">New cluster</div>
            <Input label="Cluster name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="cluster-new-name" />
            <Select label="Prime instance (control)" value={newPrime} onChange={(e) => setNewPrime(e.target.value)} data-testid="cluster-new-prime">
              <option value="">Select instance…</option>
              {licenses.map((l) => <option key={l.id} value={l.id}>{licLabel(l)}</option>)}
            </Select>
            <Button onClick={createCluster} data-testid="cluster-create"><Plus size={15} /> Create cluster</Button>
          </Card>
          <div className="space-y-2" data-testid="cluster-list">
            {clusters.length === 0 && <p className="text-sm opacity-60">No clusters yet.</p>}
            {clusters.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)} data-testid={`cluster-item-${c.id}`}
                className={`flex w-full items-center gap-2 rounded-xl border p-3 text-start lift ${sel === c.id ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                <Network size={16} className="text-[var(--primary)]" />
                <div className="flex-1"><div className="font-medium">{c.name}</div><div className="text-xs opacity-60">Prime: {c.prime_label} · {c.members.length}/12 members</div></div>
                <Trash2 size={15} className="opacity-50 hover:text-[#DA1E28]" onClick={(e) => { e.stopPropagation(); delCluster(c.id); }} data-testid={`cluster-del-${c.id}`} />
              </button>
            ))}
          </div>
        </div>

        {/* right: detail */}
        <div>
          {!current ? <Card><p className="text-sm opacity-60">Select or create a cluster to manage members and run operations.</p></Card>
            : <ClusterDetail cluster={current} licenses={licenses} licLabel={licLabel} reload={load} setMsg={setMsg} />}
        </div>
      </div>
      </CapGate>
    </div>
  );
}

function ClusterDetail({ cluster, licenses, licLabel, reload, setMsg }) {
  const [addLic, setAddLic] = useState("");
  const [addAccess, setAddAccess] = useState("both");
  const [addApps, setAddApps] = useState(["currency", "benefits", "family_tree", "holdings"]);
  const [lastCode, setLastCode] = useState(null);
  const usedLic = new Set([cluster.prime_license_id, ...cluster.members.map((m) => m.license_id)]);
  const available = licenses.filter((l) => !usedLic.has(l.id));

  const addMember = async () => {
    if (!addLic) { setMsg("Choose an instance to add."); return; }
    try {
      const { data } = await client.post(`/clusters/${cluster.id}/members`, { license_id: addLic, access: addAccess, apps: addApps });
      setLastCode({ label: data.member.runner_label, code: data.pairing_code }); setAddLic(""); reload();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const patchMember = async (m, patch) => { try { await client.put(`/clusters/${cluster.id}/members/${m.id}`, patch); reload(); } catch (e) { setMsg(apiErr(e)); } };
  const removeMember = async (m) => { if (!window.confirm(`Remove ${m.runner_label} from the cluster?`)) return; try { await client.delete(`/clusters/${cluster.id}/members/${m.id}`); reload(); } catch (e) { setMsg(apiErr(e)); } };
  const toggleApp = (a) => setAddApps((s) => s.includes(a) ? s.filter((x) => x !== a) : [...s, a]);
  const statusColor = { active: "success", invited: "warning", declined: "neutral" };

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">{cluster.name}</span>
          <Badge color="info">Prime: {cluster.prime_label}</Badge>
          <Badge color="neutral">{cluster.members.filter((m) => m.status !== "declined").length}/12</Badge>
        </div>

        {/* members table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cluster-members">
            <thead><tr className="text-start opacity-60"><th className="py-1">Member instance</th><th>Status</th><th>Access</th><th>Exposed apps</th><th></th></tr></thead>
            <tbody>
              {cluster.members.filter((m) => m.status !== "declined").map((m) => (
                <tr key={m.id} className="border-t border-black/5 dark:border-white/5" data-testid={`cluster-member-${m.id}`}>
                  <td className="py-2">{m.runner_label}</td>
                  <td><Badge color={statusColor[m.status] || "neutral"}>{m.status}</Badge></td>
                  <td>
                    <Select value={m.access} onChange={(e) => patchMember(m, { access: e.target.value })} data-testid={`cluster-access-${m.id}`} className="w-28">
                      <option value="read">Read</option><option value="write">Write</option><option value="both">Read+Write</option>
                    </Select>
                  </td>
                  <td className="text-xs">{(m.apps || []).map(appL).join(", ")}</td>
                  <td><button onClick={() => removeMember(m)} data-testid={`cluster-member-del-${m.id}`} className="opacity-60 hover:text-[#DA1E28]"><Trash2 size={15} /></button></td>
                </tr>
              ))}
              {cluster.members.filter((m) => m.status !== "declined").length === 0 && <tr><td colSpan={5} className="py-2 opacity-60">No members linked yet.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* add member */}
        <div className="rounded-xl border border-dashed border-black/15 dark:border-white/15 p-3 space-y-3">
          <div className="text-sm font-medium">Link a member instance (max 12)</div>
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <Select value={addLic} onChange={(e) => setAddLic(e.target.value)} data-testid="cluster-add-license">
              <option value="">Select instance…</option>
              {available.map((l) => <option key={l.id} value={l.id}>{licLabel(l)}</option>)}
            </Select>
            <Select value={addAccess} onChange={(e) => setAddAccess(e.target.value)} data-testid="cluster-add-access">
              <option value="read">Read</option><option value="write">Write</option><option value="both">Read+Write</option>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="cluster-add-apps">
            {APPS.map((a) => (
              <label key={a.k} className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs ${addApps.includes(a.k) ? "border-[var(--primary)] text-[var(--primary)]" : "border-black/15 dark:border-white/15"}`}>
                <input type="checkbox" checked={addApps.includes(a.k)} onChange={() => toggleApp(a.k)} data-testid={`cluster-add-app-${a.k}`} />{a.l}
              </label>
            ))}
          </div>
          <Button onClick={addMember} data-testid="cluster-add-member"><Plus size={15} /> Send pairing invite</Button>
          {lastCode && (
            <div className="rounded-md bg-[var(--primary)]/10 p-3 text-sm" data-testid="cluster-pairing-code">
              <div className="flex items-center gap-2"><KeyRound size={15} className="text-[var(--primary)]" />
                Pairing code for <b>{lastCode.label}</b>: <code className="rounded bg-black/10 px-2 py-0.5 font-mono text-base tracking-widest dark:bg-white/10">{lastCode.code}</code>
                <button onClick={() => navigator.clipboard?.writeText(lastCode.code)} className="opacity-60 hover:opacity-100"><Copy size={14} /></button>
              </div>
              <div className="mt-1 text-xs opacity-70">Share this with the member Runner. They must accept it in their Master Enabler to establish the secure link.</div>
            </div>
          )}
        </div>
      </Card>

      <ClusterOps cluster={cluster} setMsg={setMsg} reload={reload} />

      <CapGate scope="cluster" title="Enable Cluster to use the shared cache">
        <ClusterCache cluster={cluster} setMsg={setMsg} />
      </CapGate>
    </div>
  );
}

function ClusterOps({ cluster, setMsg, reload }) {
  const [collateData, setCollateData] = useState(null);
  const [opResult, setOpResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [xfer, setXfer] = useState({ amount: "", person_query: "", note: "" });
  const [rebal, setRebal] = useState({ open: false, mode: "equal", preview: null, pools: {}, targets: {} });
  const cid = cluster.id;

  const runCollate = async () => { try { const { data } = await client.get(`/clusters/${cid}/collate`); setCollateData(data); setOpResult(null); } catch (e) { setMsg(apiErr(e)); } };
  const openRebalance = async () => {
    if (rebal.open) { setRebal((r) => ({ ...r, open: false })); return; }
    try {
      const { data } = await client.get(`/clusters/${cid}/rebalance-preview`);
      const pools = {}, targets = {};
      (data.units || []).forEach((u) => { pools[u.member_id] = (u.pools[0] || {}).id || ""; targets[u.member_id] = String(u.total); });
      setRebal({ open: true, mode: "equal", preview: data, pools, targets });
    } catch (e) { setMsg(apiErr(e)); }
  };
  const runRebalance = async () => {
    const body = { mode: rebal.mode, pools: rebal.pools };
    if (rebal.mode === "targets") body.targets = Object.fromEntries(Object.entries(rebal.targets).map(([k, v]) => [k, parseFloat(v) || 0]));
    if (!window.confirm(rebal.mode === "targets" ? "Apply the specified per-unit targets to the nominated pools?" : "Rebalance the nominated pools to an equal average across write-enabled units?")) return;
    try { const { data } = await client.post(`/clusters/${cid}/rebalance`, body); setOpResult({ title: "Rebalance", data }); setRebal((r) => ({ ...r, open: false })); loadHistory(); reload(); } catch (e) { setMsg(apiErr(e)); }
  };
  const runXfer = async () => {
    const amt = parseFloat(xfer.amount);
    if (!amt || amt <= 0) { setMsg("Enter a positive transfer amount."); return; }
    try { const { data } = await client.post(`/clusters/${cid}/bulk-transfer`, { amount: amt, person_query: xfer.person_query, note: xfer.note }); setOpResult({ title: "Bulk transfer", data }); setXfer({ amount: "", person_query: "", note: "" }); loadHistory(); reload(); } catch (e) { setMsg(apiErr(e)); }
  };
  const loadHistory = useCallback(() => client.get(`/clusters/${cid}/history`).then((r) => setHistory(r.data)).catch(() => {}), [cid]);
  useEffect(() => { loadHistory(); setCollateData(null); setOpResult(null); }, [cid, loadHistory]);

  const exportReport = async (fmt) => {
    try {
      const r = await client.get(`/clusters/${cid}/export?fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `cluster_report.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
  };

  return (
    <Card className="space-y-5" data-testid="cluster-ops">
      <div className="flex items-center gap-2">
        <div className="font-head text-lg">Operations</div>
        <div className="ms-auto flex gap-2">
          <button onClick={() => exportReport("csv")} data-testid="cluster-export-csv" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><Download size={14} /> CSV</button>
          <button onClick={() => exportReport("pdf")} data-testid="cluster-export-pdf" className="flex items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm text-white lift"><FileText size={14} /> PDF report</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="subtle" onClick={runCollate} data-testid="cluster-collate"><Scale size={15} /> Collate &amp; compute</Button>
        <Button variant="subtle" onClick={openRebalance} data-testid="cluster-rebalance"><RefreshCw size={15} /> Rebalance funding pools</Button>
      </div>

      {rebal.open && (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-3" data-testid="rebalance-panel">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <RefreshCw size={15} /> Rebalance funding pools
            <div className="ms-auto flex items-center gap-2 text-xs font-normal">
              <span className="opacity-60">Mode</span>
              <Select value={rebal.mode} onChange={(e) => setRebal((r) => ({ ...r, mode: e.target.value }))} data-testid="rebalance-mode" className="w-44">
                <option value="equal">Equal average</option>
                <option value="targets">Specific targets</option>
              </Select>
            </div>
          </div>
          {(!rebal.preview || rebal.preview.units.length === 0) ? (
            <div className="text-sm opacity-60" data-testid="rebalance-empty">No write-enabled currency units to rebalance.</div>
          ) : (
            <>
              <div className="text-xs opacity-60">
                Pooled total {money(rebal.preview.grand_total)} across {rebal.preview.units.length} unit(s)
                {rebal.mode === "equal" && <> · suggested target/unit <b>{money(rebal.preview.target_per_unit)}</b></>}.
                Nominate the pool that absorbs each adjustment; balances are never driven below zero.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rebalance-table">
                  <thead><tr className="text-start opacity-60 text-xs">
                    <th className="py-1 pe-3">Unit</th><th className="py-1 pe-3">Current total</th>
                    <th className="py-1 pe-3">Adjustment pool</th>
                    {rebal.mode === "targets" && <th className="py-1 pe-3">Target total</th>}
                  </tr></thead>
                  <tbody>
                    {rebal.preview.units.map((u) => (
                      <tr key={u.member_id} className="border-t border-black/5 dark:border-white/5" data-testid={`rebalance-row-${u.member_id}`}>
                        <td className="py-1.5 pe-3 font-medium">{u.label}</td>
                        <td className="py-1.5 pe-3">{money(u.total)}</td>
                        <td className="py-1.5 pe-3">
                          <Select value={rebal.pools[u.member_id] || ""} data-testid={`rebalance-pool-${u.member_id}`}
                            onChange={(e) => setRebal((r) => ({ ...r, pools: { ...r.pools, [u.member_id]: e.target.value } }))} className="w-52">
                            {u.pools.map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.balance)}</option>)}
                          </Select>
                        </td>
                        {rebal.mode === "targets" && (
                          <td className="py-1.5 pe-3">
                            <Input type="number" value={rebal.targets[u.member_id] ?? ""} data-testid={`rebalance-target-${u.member_id}`}
                              onChange={(e) => setRebal((r) => ({ ...r, targets: { ...r.targets, [u.member_id]: e.target.value } }))} className="w-32" />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end"><Button onClick={runRebalance} data-testid="rebalance-run"><RefreshCw size={15} /> Run rebalance</Button></div>
            </>
          )}
        </div>
      )}

      {collateData && (
        <div className="overflow-x-auto rounded-xl border border-black/10 dark:border-white/10 p-3" data-testid="cluster-collate-result">
          <div className="mb-2 text-sm font-medium">Cluster collation · {collateData.units} unit(s)</div>
          <div className="grid gap-2 sm:grid-cols-4">
            {APPS.map((a) => (
              <div key={a.k} className="rounded-lg glass p-3">
                <div className="text-xs opacity-60">{a.l}</div>
                <div className="font-head text-lg">{a.k === "family_tree" ? collateData.totals[a.k].count : money(collateData.totals[a.k].total)}</div>
                <div className="text-[11px] opacity-50">avg/unit {a.k === "family_tree" ? collateData.actuarial[a.k].avg_per_unit : money(collateData.actuarial[a.k].avg_per_unit)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium"><Send size={15} /> Bulk transfer to a specified individual</div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Input label="Amount" type="number" value={xfer.amount} onChange={(e) => setXfer({ ...xfer, amount: e.target.value })} data-testid="cluster-xfer-amount" />
          <Input label="Individual (name match)" value={xfer.person_query} onChange={(e) => setXfer({ ...xfer, person_query: e.target.value })} data-testid="cluster-xfer-person" />
          <Input label="Note" value={xfer.note} onChange={(e) => setXfer({ ...xfer, note: e.target.value })} data-testid="cluster-xfer-note" />
        </div>
        <Button onClick={runXfer} data-testid="cluster-xfer-run"><Send size={15} /> Run bulk transfer</Button>
      </div>

      {opResult && (
        <div className="rounded-xl bg-black/[0.03] dark:bg-white/[0.03] p-3 text-sm" data-testid="cluster-op-result">
          <div className="mb-1 font-medium">{opResult.title}: {opResult.data.summary}</div>
          <ul className="space-y-0.5 text-xs opacity-80">
            {(opResult.data.results || []).map((r, i) => <li key={i}>{Object.entries(r).map(([k, v]) => `${k}: ${v}`).join(" · ")}</li>)}
          </ul>
        </div>
      )}

      <div data-testid="cluster-history">
        <div className="mb-1 flex items-center gap-2 text-sm font-medium"><History size={15} /> Operation history</div>
        {history.length === 0 && <p className="text-xs opacity-60">No operations run yet.</p>}
        <ul className="space-y-1 text-xs">
          {history.map((o) => <li key={o.id} className="flex gap-2"><Badge color="neutral">{o.type}</Badge><span className="opacity-70">{o.summary}</span><span className="ms-auto opacity-40">{(o.at || "").slice(0, 16).replace("T", " ")}</span></li>)}
        </ul>
      </div>

      <ClusterPresets cid={cid} setMsg={setMsg} onRun={(data, title) => { setOpResult({ title, data }); loadHistory(); }} />
    </Card>
  );
}

function ClusterPresets({ cid, setMsg, onRun }) {
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState({ name: "", type: "rebalance", frequency: "none", amount: "", person_query: "", note: "" });
  const load = useCallback(() => client.get(`/clusters/${cid}/presets`).then((r) => setPresets(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { setMsg("Name the preset."); return; }
    const params = form.type === "bulk_transfer"
      ? { amount: parseFloat(form.amount) || 0, person_query: form.person_query, note: form.note }
      : {};
    try { await client.post(`/clusters/${cid}/presets`, { name: form.name, type: form.type, frequency: form.frequency, params }); setForm({ name: "", type: "rebalance", frequency: "none", amount: "", person_query: "", note: "" }); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const run = async (p) => { try { const { data } = await client.post(`/clusters/${cid}/presets/${p.id}/run`); onRun(data, `Preset: ${p.name}`); } catch (e) { setMsg(apiErr(e)); } };
  const setSched = async (p, frequency) => { try { await client.put(`/clusters/${cid}/presets/${p.id}/schedule`, { frequency }); load(); } catch (e) { setMsg(apiErr(e)); } };
  const del = async (p) => { try { await client.delete(`/clusters/${cid}/presets/${p.id}`); load(); } catch (e) { setMsg(apiErr(e)); } };

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-3" data-testid="cluster-presets">
      <div className="flex items-center gap-2 text-sm font-medium"><Play size={15} /> Operation presets &amp; schedule</div>
      <div className="grid gap-2 sm:grid-cols-[1fr_150px_150px]">
        <Input label="Preset name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="preset-name" />
        <Select label="Operation" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="preset-type">
          <option value="rebalance">Rebalance</option><option value="bulk_transfer">Bulk transfer</option><option value="collate">Collate</option>
        </Select>
        <Select label="Schedule" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="preset-frequency">
          <option value="none">Manual only</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option>
        </Select>
      </div>
      {form.type === "bulk_transfer" && (
        <div className="grid gap-2 sm:grid-cols-3">
          <Input label="Amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="preset-amount" />
          <Input label="Individual (name match)" value={form.person_query} onChange={(e) => setForm({ ...form, person_query: e.target.value })} data-testid="preset-person" />
          <Input label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="preset-note" />
        </div>
      )}
      <Button onClick={create} data-testid="preset-create"><Plus size={15} /> Save preset</Button>
      <p className="text-[11px] opacity-55">Scheduled runs only operate on members who have manually opted in (armed) their instance.</p>

      <div className="space-y-1">
        {presets.length === 0 && <p className="text-xs opacity-60">No presets yet.</p>}
        {presets.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border border-black/5 dark:border-white/5 p-2 text-sm" data-testid={`preset-${p.id}`}>
            <Badge color="info">{p.type}</Badge>
            <span className="font-medium">{p.name}</span>
            {p.frequency && p.frequency !== "none"
              ? <Badge color="success">{p.frequency} · next {(p.next_due || "").slice(0, 10)}</Badge>
              : <Badge color="neutral">manual</Badge>}
            <Select value={p.frequency || "none"} onChange={(e) => setSched(p, e.target.value)} data-testid={`preset-sched-${p.id}`} className="w-32">
              <option value="none">Manual only</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option>
            </Select>
            <div className="ms-auto flex items-center gap-2">
              <button onClick={() => run(p)} data-testid={`preset-run-${p.id}`} className="flex items-center gap-1 rounded bg-[var(--primary)] px-2 py-1 text-xs text-white lift"><Play size={12} /> Run now</button>
              <button onClick={() => del(p)} data-testid={`preset-del-${p.id}`} className="opacity-60 hover:text-[#DA1E28]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const FUNC_LABELS = {
  overwrite: "Overwrite matching", dedupe: "Skip duplicates", reparent: "Re-parent to node",
  relabel_branch: "Relabel branch / colour", tag_group: "Tag into group", redact: "Redact dates & photos",
  map_field_labels: "Map field label", recalc_descendants: "Recalc relationships",
  convert_valuation: "Convert valuation (\u00d7 rate)", retag_type: "Re-tag holding type",
  haircut_uplift: "Haircut / uplift %", assign_category: "Assign to target category",
  recompute_dep: "Recompute depreciation", reconcile_merge: "Reconcile (merge values)", roll_up: "Roll up / consolidate",
  merge_balances: "Merge same-currency balances", convert_rate: "Convert to target rate",
  set_autotopup: "Set auto top-up", apply_fee: "Apply fee %", cap_floor: "Cap / floor balance", tag_region: "Tag region",
  net_off: "Net off balances",
  remap_pool: "Remap funding pool", adjust_amount: "Adjust monthly amount", assign_group: "Assign class / group",
  schedule_payout: "Schedule first payout",
};

function ClusterCache({ cluster, setMsg }) {
  const cid = cluster.id;
  const [meta, setMeta] = useState(null);
  const [app, setApp] = useState("family_tree");
  // export
  const [srcUnit, setSrcUnit] = useState("");
  const [records, setRecords] = useState([]);
  const [picked, setPicked] = useState({});
  const [mode, setMode] = useState("copy");
  // cache + import
  const [cacheItems, setCacheItems] = useState([]);
  const [pickedCache, setPickedCache] = useState({});
  const [tgtUnit, setTgtUnit] = useState("");
  const [targets, setTargets] = useState({ kind: "none", options: [] });
  const [target, setTarget] = useState({});
  const [funcs, setFuncs] = useState([]);
  const [params, setParams] = useState({});
  const [result, setResult] = useState(null);
  const [bin, setBin] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);

  const loadMeta = useCallback(() => client.get(`/clusters/${cid}/cache/meta`).then((r) => setMeta(r.data)).catch((e) => setMsg(apiErr(e))), [cid, setMsg]);
  const loadCache = useCallback(() => client.get(`/clusters/${cid}/cache/${app}`).then((r) => setCacheItems(r.data)).catch(() => {}), [cid, app]);
  const loadBin = useCallback(() => client.get(`/clusters/${cid}/recently-moved`).then((r) => setBin(r.data)).catch(() => {}), [cid]);
  const loadActivity = useCallback(() => client.get(`/clusters/${cid}/cache-activity`).then((r) => setActivity(r.data)).catch(() => {}), [cid]);
  useEffect(() => { loadMeta(); loadBin(); loadActivity(); }, [loadMeta, loadBin, loadActivity]);
  useEffect(() => { loadCache(); setPicked({}); setRecords([]); setPickedCache({}); setFuncs([]); setParams({}); setResult(null); setTarget({}); }, [app, loadCache]);

  const units = meta?.units || [];
  const importUnits = units.filter((u) => u.access === "write" || u.access === "both");
  const availFuncs = meta?.functions?.[app] || [];

  const loadRecords = async (uid) => {
    setSrcUnit(uid); setPicked({});
    if (!uid) { setRecords([]); return; }
    try { const { data } = await client.get(`/clusters/${cid}/cache/${app}/records`, { params: { unit_id: uid } }); setRecords(data); } catch (e) { setMsg(apiErr(e)); }
  };
  const loadTargets = async (uid) => {
    setTgtUnit(uid); setTarget({});
    if (!uid) { setTargets({ kind: "none", options: [] }); return; }
    try { const { data } = await client.get(`/clusters/${cid}/cache/${app}/targets`, { params: { unit_id: uid } }); setTargets(data); } catch (e) { setMsg(apiErr(e)); }
  };

  const doExport = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (!srcUnit || ids.length === 0) { setMsg("Pick a source unit and at least one item."); return; }
    if (mode === "move" && !window.confirm(`Move ${ids.length} item(s)? The source records are deleted only after a successful import.`)) return;
    try {
      const { data } = await client.post(`/clusters/${cid}/cache/${app}/export`, { source_unit_id: srcUnit, record_ids: ids, mode });
      setMsg(`Exported ${data.exported} item(s) to the cache (${data.mode}).`); setPicked({}); loadCache(); loadMeta(); loadActivity();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const doImport = async () => {
    const ids = Object.keys(pickedCache).filter((k) => pickedCache[k]);
    if (!tgtUnit || ids.length === 0) { setMsg("Pick a target unit and at least one cached item."); return; }
    try {
      const { data } = await client.post(`/clusters/${cid}/cache/${app}/import`, { target_unit_id: tgtUnit, cache_item_ids: ids, target, functions: funcs, params });
      setResult(data); setMsg(`Imported ${data.imported} item(s).`); setPickedCache({}); loadCache(); loadMeta(); loadBin(); loadActivity();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const restoreMoved = async (rmid) => {
    try { const { data } = await client.post(`/clusters/${cid}/recently-moved/${rmid}/restore`); setMsg(`Restored ${data.label}.`); loadBin(); } catch (e) { setMsg(apiErr(e)); }
  };
  const clearCache = async () => {
    if (!window.confirm(`Clear all cached ${appL(app)} items?`)) return;
    try { await client.post(`/clusters/${cid}/cache/${app}/clear`, { cache_item_ids: [] }); loadCache(); loadMeta(); } catch (e) { setMsg(apiErr(e)); }
  };
  const toggleFunc = (f) => setFuncs((s) => s.includes(f) ? s.filter((x) => x !== f) : [...s, f]);
  const setP = (k, v) => setParams((s) => ({ ...s, [k]: v }));
  const has = (f) => funcs.includes(f);

  return (
    <Card className="space-y-5" data-testid="cluster-cache">
      <div className="flex items-center gap-2">
        <Boxes size={18} className="text-[var(--primary)]" />
        <div className="font-head text-lg">Cluster cache — import / export</div>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="cache-app-tabs">
        {APPS.map((a) => (
          <button key={a.k} onClick={() => setApp(a.k)} data-testid={`cache-app-${a.k}`}
            className={`rounded-full px-3 py-1 text-sm lift ${app === a.k ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>
            {a.l}{meta?.counts?.[a.k] ? ` (${meta.counts[a.k]})` : ""}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* EXPORT */}
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-3" data-testid="cache-export-panel">
          <div className="flex items-center gap-2 text-sm font-medium"><Upload size={15} /> Export to cache</div>
          <Select label="Source unit" value={srcUnit} onChange={(e) => loadRecords(e.target.value)} data-testid="cache-src-unit">
            <option value="">Select a unit…</option>
            {units.filter((u) => u.apps.includes(app)).map((u) => <option key={u.id} value={u.id}>{u.label} ({u.role})</option>)}
          </Select>
          <div className="max-h-44 overflow-y-auto rounded-md border border-black/10 dark:border-white/10 p-2 space-y-1" data-testid="cache-records">
            {records.length === 0 && <p className="text-xs opacity-60">No items — pick a source unit.</p>}
            {records.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`cache-rec-${r.id}`}>
                <input type="checkbox" checked={!!picked[r.id]} onChange={() => setPicked((s) => ({ ...s, [r.id]: !s[r.id] }))} />
                <span>{r.label}</span>{r.sub && <span className="text-[11px] opacity-50">{r.sub}</span>}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="cache-mode" className="w-40">
              <option value="copy">Copy (keep source)</option>
              <option value="move">Move (remove source)</option>
            </Select>
            <Button onClick={doExport} data-testid="cache-export-btn"><Upload size={15} /> Export</Button>
          </div>
        </div>

        {/* CACHE + IMPORT */}
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-3" data-testid="cache-import-panel">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ArrowLeftRight size={15} /> Cached items ({cacheItems.length})
            {cacheItems.length > 0 && <button onClick={clearCache} data-testid="cache-clear" className="ms-auto text-xs opacity-60 hover:text-[#DA1E28]">Clear all</button>}
          </div>
          <div className="max-h-40 overflow-y-auto rounded-md border border-black/10 dark:border-white/10 p-2 space-y-1" data-testid="cache-items">
            {cacheItems.length === 0 && <p className="text-xs opacity-60">Cache is empty for {appL(app)}.</p>}
            {cacheItems.map((it) => (
              <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`cache-item-${it.id}`}>
                <input type="checkbox" checked={!!pickedCache[it.id]} onChange={() => setPickedCache((s) => ({ ...s, [it.id]: !s[it.id] }))} />
                <span>{it.label}</span>
                <Badge color={it.mode === "move" ? "warning" : "neutral"}>{it.mode}</Badge>
                <span className="ms-auto text-[11px] opacity-50">from {it.source_label}</span>
              </label>
            ))}
          </div>
          <Select label="Target unit (write access)" value={tgtUnit} onChange={(e) => loadTargets(e.target.value)} data-testid="cache-tgt-unit">
            <option value="">Select a unit…</option>
            {importUnits.filter((u) => u.apps.includes(app)).map((u) => <option key={u.id} value={u.id}>{u.label} ({u.role})</option>)}
          </Select>
          {targets.kind === "tree" && (
            <Select label="Target tree" value={target.tree_id || ""} onChange={(e) => setTarget({ tree_id: e.target.value, tree_type: (targets.options.find((o) => o.id === e.target.value) || {}).tree_type })} data-testid="cache-target-tree">
              <option value="">(keep original tree)</option>
              {targets.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </Select>
          )}
          {targets.kind === "category" && (
            <Input label="Target category (type to create)" value={target.category || ""} onChange={(e) => setTarget({ category: e.target.value })} data-testid="cache-target-category" />
          )}
          {targets.kind === "klass" && (
            <Input label="Target class / listing" value={target.klass || ""} onChange={(e) => setTarget({ klass: e.target.value })} data-testid="cache-target-klass" />
          )}
          <div className="space-y-1">
            <div className="text-xs font-medium opacity-70">Functions on import</div>
            <div className="flex flex-wrap gap-1.5" data-testid="cache-functions">
              {availFuncs.map((f) => (
                <button key={f} onClick={() => toggleFunc(f)} data-testid={`cache-func-${f}`}
                  className={`rounded-full px-2.5 py-1 text-xs lift ${has(f) ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>
                  {FUNC_LABELS[f] || f}
                </button>
              ))}
            </div>
          </div>
          {/* dynamic params */}
          <div className="grid gap-2 sm:grid-cols-2">
            {(has("convert_valuation") || has("convert_rate")) && <Input label="Rate (×)" type="number" value={params.rate || ""} onChange={(e) => setP("rate", e.target.value)} data-testid="cache-p-rate" />}
            {has("haircut_uplift") && <Input label="Haircut/uplift %" type="number" value={params.pct || ""} onChange={(e) => setP("pct", e.target.value)} data-testid="cache-p-pct" />}
            {has("apply_fee") && <Input label="Fee %" type="number" value={params.fee_pct || ""} onChange={(e) => setP("fee_pct", e.target.value)} data-testid="cache-p-fee" />}
            {has("adjust_amount") && <>
              <Select label="Amount mode" value={params.amount_mode || "pct"} onChange={(e) => setP("amount_mode", e.target.value)} data-testid="cache-p-amtmode"><option value="pct">Uplift %</option><option value="set">Set value</option></Select>
              <Input label={params.amount_mode === "set" ? "Amount" : "Uplift %"} type="number" value={(params.amount_mode === "set" ? params.amount : params.pct) || ""} onChange={(e) => setP(params.amount_mode === "set" ? "amount" : "pct", e.target.value)} data-testid="cache-p-amt" />
            </>}
            {has("reparent") && <Input label="Parent record id" value={params.parent_id || ""} onChange={(e) => setP("parent_id", e.target.value)} data-testid="cache-p-parent" />}
            {has("relabel_branch") && <><Input label="Branch" value={params.branch || ""} onChange={(e) => setP("branch", e.target.value)} data-testid="cache-p-branch" /><Input label="Colour (#hex)" value={params.color || ""} onChange={(e) => setP("color", e.target.value)} data-testid="cache-p-color" /></>}
            {has("tag_group") && <Input label="Group tag" value={params.group || ""} onChange={(e) => setP("group", e.target.value)} data-testid="cache-p-group" />}
            {has("retag_type") && <Input label="Holding type" value={params.trust || ""} onChange={(e) => setP("trust", e.target.value)} data-testid="cache-p-trust" />}
            {has("tag_region") && <Input label="Region" value={params.region || ""} onChange={(e) => setP("region", e.target.value)} data-testid="cache-p-region" />}
            {has("remap_pool") && <Input label="Funding pool" value={params.funding_pool || ""} onChange={(e) => setP("funding_pool", e.target.value)} data-testid="cache-p-pool" />}
            {has("assign_group") && <Input label="Class / group" value={params.klass || ""} onChange={(e) => setP("klass", e.target.value)} data-testid="cache-p-klass" />}
            {has("cap_floor") && <><Input label="Cap" type="number" value={params.cap || ""} onChange={(e) => setP("cap", e.target.value)} data-testid="cache-p-cap" /><Input label="Floor" type="number" value={params.floor || ""} onChange={(e) => setP("floor", e.target.value)} data-testid="cache-p-floor" /></>}
            {has("roll_up") && <Input label="Consolidated name" value={params.rollup_name || ""} onChange={(e) => setP("rollup_name", e.target.value)} data-testid="cache-p-rollup" />}
            {has("map_field_labels") && <><Input label="Field from" value={params.label_from || ""} onChange={(e) => setP("label_from", e.target.value)} data-testid="cache-p-lfrom" /><Input label="Field to" value={params.label_to || ""} onChange={(e) => setP("label_to", e.target.value)} data-testid="cache-p-lto" /></>}
            {has("schedule_payout") && <Input label="First payout date" type="date" value={params.payout_date || ""} onChange={(e) => setP("payout_date", e.target.value)} data-testid="cache-p-payout" />}
          </div>
          <Button onClick={doImport} data-testid="cache-import-btn"><ArrowLeftRight size={15} /> Import selected</Button>
        </div>
      </div>

      {result && (
        <div className="rounded-xl bg-black/[0.03] dark:bg-white/[0.03] p-3 text-sm" data-testid="cache-result">
          <div className="mb-1 font-medium">Imported {result.imported} item(s)</div>
          <ul className="space-y-0.5 text-xs opacity-80">
            {(result.results || []).map((r, i) => <li key={i}>{r.item} — {r.status}{r.reason ? ` (${r.reason})` : ""}</li>)}
          </ul>
        </div>
      )}
      <p className="text-[11px] opacity-55">Each app has its own typed cache. Export copies (or moves) selected items into the cache; import writes them into the chosen target location, applying any selected functions. Move deletes only the specific source record after a successful import — never a whole tree.</p>

      {bin.length > 0 && (
        <div className="rounded-xl border border-[#F1C21B]/40 bg-[#F1C21B]/10 p-3 space-y-2" data-testid="recently-moved">
          <div className="text-sm font-medium flex items-center gap-2"><History size={15} /> Recently moved — restorable for 24h ({bin.length})</div>
          {bin.map((b) => (
            <div key={b.id} className="flex items-center gap-2 text-sm" data-testid={`moved-${b.id}`}>
              <span>{b.label}</span>
              <Badge color="neutral">{b.app}</Badge>
              <span className="text-[11px] opacity-50">from {b.source_label}</span>
              <Button variant="ghost" className="ms-auto" onClick={() => restoreMoved(b.id)} data-testid={`restore-${b.id}`}><RefreshCw size={14} /> Restore</Button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-black/10 dark:border-white/10" data-testid="cache-activity-tab">
        <button onClick={() => setShowActivity((v) => !v)} className="flex w-full items-center gap-2 p-3 text-sm font-medium" data-testid="cache-activity-toggle">
          <History size={15} /> Cache activity ({activity.length})
          <span className="ms-auto text-xs opacity-60">{showActivity ? "Hide" : "Show"}</span>
        </button>
        {showActivity && (
          <div className="max-h-64 overflow-y-auto border-t border-black/10 dark:border-white/10 p-3 space-y-1.5" data-testid="cache-activity-list">
            {activity.length === 0 && <p className="text-xs opacity-60">No cache activity yet.</p>}
            {activity.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-xs" data-testid={`cache-activity-${i}`}>
                <Badge color={a.type === "cache_import" ? "success" : "neutral"}>{a.type === "cache_import" ? "import" : "export"}</Badge>
                <span className="opacity-90">{a.summary}</span>
                <span className="ms-auto whitespace-nowrap opacity-50">{a.actor} · {new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
